/**
 * persistentStorage — ask the browser not to evict this till's database.
 *
 * Every other client owns its storage: Desktop has a filesystem, Mobile has an
 * app sandbox. A browser does not make that promise. By default IndexedDB is
 * "best-effort" storage, which the user agent may discard under storage
 * pressure, and the user can wipe with Clear site data.
 *
 * For a POS that is not a cache — it is the shop's books between syncs. Evicting
 * it loses unsynced sales outright, and silently resets the invoice sequence, so
 * the device re-issues numbers it has already used (see
 * billing/invoiceNumberRecovery.ts, which repairs the sequence afterwards; this
 * tries to stop it happening at all).
 *
 * `navigator.storage.persist()` upgrades the origin to "persistent", which the
 * browser will not evict automatically. Whether it is granted is the browser's
 * call — Chrome grants it silently for installed or frequently-visited sites,
 * Firefox prompts, Safari applies its own rules — so this is an ask, not a
 * guarantee, and everything downstream still assumes storage can vanish.
 */

export type PersistenceResult =
  | { supported: false }
  | { supported: true; persisted: boolean };

/**
 * Request persistent storage. Safe to call on every boot: when the origin is
 * already persisted the browser answers from its own state without a prompt.
 */
export async function requestPersistentStorage(): Promise<PersistenceResult> {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.persist) {
      return { supported: false };
    }

    // Already granted? Asking again can re-prompt on some browsers.
    if (await navigator.storage.persisted?.()) {
      return { supported: true, persisted: true };
    }

    const persisted = await navigator.storage.persist();
    if (!persisted) {
      // Not an error: the shop keeps working, it is just one browser
      // housekeeping pass away from losing unsynced data. Worth saying out loud
      // because the usual cause — a site the merchant has not "installed" or
      // visited often — is fixable by installing the PWA.
      console.warn(
        '[Storage] Persistent storage refused. The browser may evict this ' +
        'till\'s database under storage pressure; unsynced sales would be lost. ' +
        'Installing the app usually earns the grant.',
      );
    }
    return { supported: true, persisted };
  } catch (err) {
    // Private windows and blocked site data both throw here.
    console.warn('[Storage] Could not request persistence:', (err as Error).message);
    return { supported: false };
  }
}
